import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { adminBookingSchema } from '../../../utils/validationSchemas';
import { useBookingStore } from '../../../store/bookingStore';
import { checkTimeSlotAvailability } from '../../../services/availability';
import { formatISO, formatDateBR, isSameDay } from '../../../utils/dateUtils';
import { createOrUpdateClientAndBook } from '../../../firebase/clients';

// Componentes de etapas
import ServiceSelection from '../../booking/steps/ServiceSelection';
import DateTimeSelection from '../../booking/steps/DateTimeSelection';
import AdminClientForm from './AdminClientForm';
import ProgressIndicator from '../../booking/ProgressIndicator';

/**
 * Adaptador para BookingPage no contexto administrativo
 * Permite reutilizar os componentes do fluxo de agendamento do cliente
 * com funcionalidades adicionais para administradores
 */
const AdminBookingFormAdapter = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  mode = 'new', // 'new', 'reschedule', 'editServices'
  services = [],
  professionals = [],
  salonId,
  isModal = true
}) => {
  // Estados
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Acesso ao store de booking
  const resetBooking = useBookingStore((state) => state.resetBooking);
  const setStep = useBookingStore((state) => state.setStep);
  const step = useBookingStore((state) => state.step);
  const nextStep = useBookingStore((state) => state.nextStep);
  const prevStep = useBookingStore((state) => state.prevStep);
  
  // Configs para modo admin
  const adminConfig = {
    isAdmin: true,
    salonConfig: { primaryColor: "#3B82F6" },
    theme: 'admin'
  };

  // Form context com React Hook Form
  const methods = useForm({
    resolver: zodResolver(adminBookingSchema),
    mode: 'onChange',
    defaultValues: {
      services: [],
      date: null,
      time: null,
      professional: null,
      client: {
        name: '',
        phone: '',
        email: '',
        notes: ''
      },
      // Campos adicionais para admin
      confirmed: false,
      paymentConfirmed: false
    }
  });

  // Monitorar a mudança de data no store para atualizar a confirmação automática
  const selectedDate = useBookingStore((state) => state.date);

  // Verificar se a data selecionada é o dia atual para auto-confirmar
  useEffect(() => {
    if (step === 3 && selectedDate) {
      const isToday = isSameDay(selectedDate, new Date());
      if (isToday) {
        methods.setValue('confirmed', true);
      }
    }
  }, [step, selectedDate, methods]);

  // Adicionar logs para verificação de parâmetros ao inicializar o adaptador
  console.log(`[AdminBookingFormAdapter] Inicializando com modo=${mode}, excludeAppointmentId=${initialData?.id || 'não definido'}`);
  if (initialData) {
    console.log(`[AdminBookingFormAdapter] initialData:`, {
      id: initialData.id,
      data: initialData.data,
      horario: initialData.horario,
      servicos: initialData.servicos,
      servicosType: typeof initialData.servicos,
      isArray: Array.isArray(initialData.servicos),
      profissional: initialData.profissional,
      excludeAppointmentId: initialData.excludeAppointmentId,
      allowConflicts: initialData.allowConflicts,
      skipAvailabilityCheck: initialData.skipAvailabilityCheck
    });
    
    // Log dos serviços para debug
    if (initialData.servicos) {
      console.log(`[AdminBookingFormAdapter] Serviços recebidos detalhados:`, initialData.servicos);
    }
  }

  // Inicializar formulário com dados existentes quando disponíveis
  useEffect(() => {
    if (isOpen && initialData) {
      // Configurar a etapa inicial com base no modo
      if (mode === 'reschedule') {
        setStep(2);
      } else if (mode === 'editServices') {
        setStep(1);
      } else {
        setStep(1);
      }

      // Mapear dados iniciais para o formato do formulário
      let mappedServices = [];
      
      if (initialData.servicos) {
        // Se já for um array, usá-lo diretamente
        if (Array.isArray(initialData.servicos)) {
          mappedServices = initialData.servicos;
          console.log(`[AdminBookingFormAdapter] Usando servicos como array:`, mappedServices);
        } 
        // Se for um objeto único, convertê-lo para array
        else if (typeof initialData.servicos === 'object' && initialData.servicos !== null) {
          mappedServices = [initialData.servicos];
          console.log(`[AdminBookingFormAdapter] Convertendo objeto para array:`, mappedServices);
        }
        // Se for uma string ou número, convertê-lo para array com esse valor
        else {
          mappedServices = [initialData.servicos];
          console.log(`[AdminBookingFormAdapter] Convertendo valor único para array:`, mappedServices);
        }
      }

      const dateObj = initialData.data 
        ? new Date(initialData.data) 
        : null;

      // Log para verificar o que está sendo usado para preencher o formulário
      console.log(`[AdminBookingFormAdapter] Dados de formulário antes do reset:`, {
        services: mappedServices,
        servicesType: typeof mappedServices,
        isArray: Array.isArray(mappedServices),
        date: dateObj,
        time: initialData.horario || null
      });

      // Preencher o formulário com dados existentes
      methods.reset({
        services: mappedServices,
        date: dateObj,
        time: initialData.horario || null,
        professional: initialData.profissional || null,
        client: {
          name: initialData.cliente || '',
          phone: initialData.whatsapp || '',
          email: initialData.email || '',
          notes: initialData.observacoes || ''
        },
        confirmed: initialData.confirmado || false,
        paymentConfirmed: initialData.pagamentoConfirmado || false
      });
      
      // Log do estado do formulário após reset
      console.log(`[AdminBookingFormAdapter] Formulário após reset:`, methods.getValues());
    } else if (isOpen) {
      // Reset para novo agendamento
      resetBooking();
      methods.reset();
      setStep(1);
    }
  }, [isOpen, initialData, mode, methods, resetBooking, setStep]);

  // Função para processar o formulário
  const handleSubmitBooking = async (data) => {
    try {
      console.log('[AdminBookingFormAdapter] handleSubmitBooking iniciado com:', {
        modo: mode,
        dataRecebida: data ? 'presente' : 'undefined',
        dataKeys: data ? Object.keys(data) : 'N/A',
        salonId,
        initialDataPresente: initialData ? 'sim' : 'não',
        step: step,
      });
      
      setLoading(true);
      setError(null);
      
      // Diferentes comportamentos baseados no modo
      if (mode === 'editServices') {
        // Logs para editServices
        console.log('[AdminBookingFormAdapter] Modo editServices com dados:', {
          services: data?.services,
          initialDataId: initialData?.id
        });
        
        // Apenas enviar serviços atualizados
        if (!data.services || data.services.length === 0) {
          setError('Selecione pelo menos um serviço');
          return false;
        }

        onSubmit({
          ...initialData,
          servicos: data.services.map(s => typeof s === 'object' ? s.id : s),
          servicosDetalhes: data.services.map(s => {
            if (typeof s !== 'object') return null;
            return {
              id: s.id,
              nome: s.nome,
              preco: s.preco || 0,
              duracao: s.duracao || 0
            };
          }).filter(Boolean)
        });

        setSuccess(true);
        return true;
      }

      if (mode === 'reschedule') {
        console.log('[AdminBookingFormAdapter] Modo reschedule com dados completos:', {
          date: data?.date,
          time: data?.time,
          professional: data?.professional,
          services: data?.services,
          initialDataId: initialData?.id,
          excludeAppointmentId: initialData?.excludeAppointmentId || initialData?.id
        });
        
        // Verificar disponibilidade específica para este horário
        const dateStr = data?.date ? formatISO(data.date) : null;
        console.log('[AdminBookingFormAdapter] dateStr gerado:', dateStr);
        
        const professionalId = data?.professional ? (
          typeof data.professional === 'object' ? data.professional.id : data.professional
        ) : null;
        console.log('[AdminBookingFormAdapter] professionalId extraído:', professionalId);

        const serviceIds = data?.services ? (
          Array.isArray(data.services) ? data.services.map(s => typeof s === 'object' ? s.id : s) : []
        ) : [];
        console.log('[AdminBookingFormAdapter] serviceIds extraídos:', serviceIds);

        // Verificar disponibilidade excluindo o agendamento atual
        console.log('[AdminBookingFormAdapter] Verificando disponibilidade com parâmetros:', {
          salonId,
          dateStr,
          time: data?.time,
          professionalId,
          serviceIds,
          excludeId: initialData?.id
        });
        
        const availability = await checkTimeSlotAvailability(
          salonId,
          dateStr,
          data?.time,
          professionalId,
          serviceIds,
          initialData?.id // ID a excluir da verificação
        );
        
        console.log('[AdminBookingFormAdapter] Resultado da verificação de disponibilidade:', availability);

        if (!availability.available) {
          // Permitir admin sobrepor verificação de disponibilidade
          data.temConflitos = true;
          data.conflitos = availability.conflicts || [];
        }

        // Log dos dados antes de construir objeto para envio
        console.log('[AdminBookingFormAdapter] initialData antes de envio:', initialData);
        console.log('[AdminBookingFormAdapter] Preparando objeto para remarcação');
        
        // Construir o objeto para remarcação com verificações extras
        const updatedAppointment = {
          ...(initialData || {}),
          data: dateStr,
          dataISO: dateStr,
          dataFormatada: data?.date ? formatDateBR(data.date) : '',
          horario: data?.time || '',
          profissional: professionalId
        };
        
        console.log('[AdminBookingFormAdapter] Objeto de remarcação construído:', updatedAppointment);
        
        // Enviar apenas data, hora e profissional atualizados
        console.log('[AdminBookingFormAdapter] Chamando onSubmit para remarcação');
        onSubmit(updatedAppointment);
        console.log('[AdminBookingFormAdapter] onSubmit completado com sucesso');

        setSuccess(true);
        return true;
      }

      // Modo novo agendamento (padrão)
      // Extrair dados para verificação de disponibilidade
      const dateStr = formatISO(data.date);
      const professionalId = typeof data.professional === 'object'
        ? data.professional.id
        : data.professional;

      const serviceIds = data.services.map(s => typeof s === 'object' ? s.id : s);

      // Verificar disponibilidade
      const availability = await checkTimeSlotAvailability(
        salonId,
        dateStr,
        data.time,
        professionalId,
        serviceIds
      );

      // Mesmo que não esteja disponível, permitir admin agendar com flag de conflito
      if (!availability.available) {
        data.temConflitos = true;
        data.conflitos = availability.conflicts || [];
      }

      // Estruturar os dados para o formato esperado
      const appointmentData = {
        cliente: {
          nome: data.client.name,
          celular: data.client.phone,
          email: data.client.email || "",
        },
        observacoes: data.client.notes || "",
        data: dateStr,
        dataFormatada: formatDateBR(data.date),
        horario: data.time,
        profissional: professionalId,
        servicos: serviceIds,
        servicosDetalhes: data.services.map(s => {
          if (typeof s !== 'object') return null;
          return {
            id: s.id,
            nome: s.nome,
            preco: s.preco || 0,
            duracao: s.duracao || 0
          };
        }).filter(Boolean),
        origem: "admin",
        confirmado: data.confirmed || false,
        pagamentoConfirmado: data.paymentConfirmed || false,
        deviceType: navigator.userAgent.match(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i)
          ? 'mobile' : 'desktop',
        temConflitos: data.temConflitos || false,
        conflitos: data.conflitos || []
      };

      // Enviar para createOrUpdateClientAndBook
      const result = await createOrUpdateClientAndBook(salonId, appointmentData);
      
      if (!result.success) {
        throw new Error(result.error || "Erro ao criar agendamento");
      }

      setSuccess(true);
      return true;
    } catch (error) {
      console.error('Erro ao processar agendamento:', error);
      setError(error.message || 'Erro ao processar agendamento');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Manipulador para voltar ou fechar
  const handleBack = () => {
    if (step > 1) {
      prevStep();
    } else {
      onClose?.();
    }
  };

  // Antes da renderização do componente DateTimeSelection, verificar e gerar o excludeAppointmentId
  let excludeAppointmentId = null;
  if (step === 2 && mode === 'reschedule' && initialData?.id) {
    excludeAppointmentId = initialData.id;
    console.log(`[AdminBookingFormAdapter] Detectada remarcação, usando excludeAppointmentId=${excludeAppointmentId}`);
    
    // Se o ID existir tanto no initialData.id quanto no initialData.excludeAppointmentId, priorizar o excludeAppointmentId
    if (initialData.excludeAppointmentId) {
      excludeAppointmentId = initialData.excludeAppointmentId;
      console.log(`[AdminBookingFormAdapter] Substituindo pelo excludeAppointmentId explícito=${excludeAppointmentId}`);
    }
    
    // Verificar se o excludeAppointmentId parece válido
    if (typeof excludeAppointmentId !== 'string' || excludeAppointmentId.length < 5) {
      console.warn(`[AdminBookingFormAdapter] excludeAppointmentId parece inválido: ${excludeAppointmentId}`);
    } else {
      console.log(`[AdminBookingFormAdapter] excludeAppointmentId válido: ${excludeAppointmentId}`);
    }
  }

  // Antes da renderização do componente DateTimeSelection, verificar as propriedades
  console.log(`[AdminBookingFormAdapter] Propriedades para DateTimeSelection:`, {
    excludeAppointmentId: step === 2 ? (initialData?.id || initialData?.excludeAppointmentId) : undefined,
    initialDate: step === 2 ? (initialData?.data || null) : null,
    initialTime: step === 2 ? (initialData?.horario || null) : null,
    isAdmin: true
  });

  // Renderização condicional baseada na etapa atual
  return (
    <div className="w-full h-full flex flex-col">
      <FormProvider {...methods}>
        {/* Cabeçalho e Indicador de Progresso */}
        <div className="flex flex-col">
          <div className="flex items-center justify-between px-4 py-2 border-b">
            <button 
              onClick={handleBack}
              className="text-gray-700"
            >
              {step === 1 ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              )}
            </button>
            <h1 className="text-lg font-semibold">
              {step === 1 && (mode === 'editServices' ? "Editar Serviços" : "Selecionar Serviços")}
              {step === 2 && (mode === 'reschedule' ? "Remarcação" : "Data e Profissional")}
              {step === 3 && "Dados do Cliente"}
            </h1>
            <div className="w-6"></div>
          </div>
          
          <ProgressIndicator 
            currentStep={step} 
            maxSteps={mode === 'editServices' ? 1 : (mode === 'reschedule' ? 2 : 3)}
            isAdmin={true}
            primaryColor="#3B82F6"
            simpleMode={true}
          />
        </div>

        {/* Conteúdo das etapas */}
        <div className="flex-1 overflow-y-auto p-4">
          {step === 1 && (
            <ServiceSelection 
              salonId={salonId}
              onContinue={(formData) => {
                console.log('[AdminBookingFormAdapter] ServiceSelection onContinue com dados:', 
                  mode === 'editServices' ? 'Enviando para handleSubmitBooking' : 'Avançando para próxima etapa', 
                  formData);
                
                return mode === 'editServices' ? handleSubmitBooking(formData) : nextStep();
              }}
              isAdmin={true}
              services={services}
              salonConfig={{ primaryColor: "#3B82F6" }}
              theme="admin"
            />
          )}
          
          {step === 2 && (
            <DateTimeSelection 
              salonId={salonId}
              onContinue={(formData) => {
                console.log('[AdminBookingFormAdapter] DateTimeSelection onContinue com dados:', {
                  modoAtual: mode,
                  formData: formData ? 'presente' : 'undefined',
                  formDataKeys: formData ? Object.keys(formData) : 'N/A',
                  excludeAppointmentId
                });
                
                // Se for remarcação, enviar diretamente para submit
                if (mode === 'reschedule') {
                  // Garantir que os dados do formulário estão disponíveis
                  if (!formData) {
                    console.error('[AdminBookingFormAdapter] Erro: formData undefined no onContinue');
                    
                    // Tentar obter os dados do hook form
                    const currentFormData = methods.getValues();
                    console.log('[AdminBookingFormAdapter] Tentando utilizar dados do formulário:', currentFormData);
                    
                    return handleSubmitBooking(currentFormData);
                  }
                  
                  return handleSubmitBooking(formData);
                }
                
                return nextStep();
              }}
              onBack={prevStep}
              isAdmin={true}
              professionals={professionals}
              salonConfig={{ primaryColor: "#3B82F6" }}
              theme="admin"
              excludeAppointmentId={excludeAppointmentId}
            />
          )}
          
          {step === 3 && (
            <AdminClientForm 
              salonId={salonId}
              onBack={prevStep}
              onSubmit={handleSubmitBooking}
              isSubmitting={loading}
              submitError={error}
              isAdmin={true}
              salonConfig={{ primaryColor: "#3B82F6" }}
              theme="admin"
            />
          )}
        </div>
      </FormProvider>
    </div>
  );
};

export default AdminBookingFormAdapter; 